/**
 * AB#21597 — a duration given in DAYS, rendered as days + hours.
 *
 * The measured stage timings arrive as fractional days (`0.4`, `67.9`). Rendered
 * literally — "67.9 days", "0.4 days" — they read badly: a fractional day is not
 * a unit people think in. People think in days and hours. This converts the
 * fraction into exactly that.
 *
 * Rules: round to the nearest hour; roll 24h up into a day; drop a zero part
 * (`2d`, not `2d 0h`; `10h`, not `0d 10h`). Under an hour it falls back to
 * minutes so a stage that has just started does not read as a flat `0h`. Zero or
 * negative collapses to `0h`; a non-finite value to an em dash.
 *
 *   formatDaysHours(67.9) === '67d 22h'
 *   formatDaysHours(2.8)  === '2d 19h'
 *   formatDaysHours(2)    === '2d'
 *   formatDaysHours(0.4)  === '10h'
 *   formatDaysHours(0.01) === '14m'
 */
export function formatDaysHours(days: number | null | undefined): string {
  if (days == null || Number.isNaN(days) || !Number.isFinite(days)) return '—';
  if (days <= 0) return '0h';

  const totalMinutes = Math.round(days * 24 * 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;

  let d = Math.floor(totalMinutes / (24 * 60));
  let h = Math.round((totalMinutes - d * 24 * 60) / 60);
  if (h === 24) {
    d += 1;
    h = 0;
  }

  if (d === 0) return `${h}h`;
  if (h === 0) return `${d}d`;
  return `${d}d ${h}h`;
}

/**
 * A range of durations (both in days) as days + hours: `10h–2d 19h`. An equal
 * low and high collapses to the single value, so a tight band does not read as
 * "2d–2d".
 */
export function formatDaysHoursRange(
  low: number | null | undefined,
  high: number | null | undefined
): string {
  const lo = formatDaysHours(low);
  const hi = formatDaysHours(high);
  return lo === hi ? hi : `${lo}–${hi}`;
}
